import type { AccountStatus, Role } from '@travel-planner/shared/schemas/auth';
import { accountStatuses, roles } from '@travel-planner/shared/schemas/auth';

export type AccountSortField = 'name' | 'email' | 'roles' | 'status';
export type SortOrder = 'asc' | 'desc';

export type UsersSearch = {
  search?: string;
  status?: AccountStatus[];
  roles?: Role[];
  page?: number;
  pageSize?: number;
  sortBy?: AccountSortField;
  sortOrder?: SortOrder;
  // Whether soft-deleted (suspended) accounts are listed. Absent/false hides
  // them; the Accounts table turns it on so an admin can reach Restore.
  includeDeleted?: boolean;
};

// Single source for the Accounts pagination page-size — the validator clamps
// URL values to this set and the table builds its "Results per page" options
// from it, so the two never drift.
export const USERS_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;
export const DEFAULT_USERS_PAGE_SIZE = 20;

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const asPage = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 1) return v;
  if (typeof v === 'string' && /^\d+$/.test(v)) {
    const n = Number.parseInt(v, 10);
    return n >= 1 ? n : undefined;
  }
  return undefined;
};

const asPageSize = (v: unknown): number | undefined => {
  const n =
    typeof v === 'number' && Number.isInteger(v)
      ? v
      : typeof v === 'string' && /^\d+$/.test(v)
        ? Number.parseInt(v, 10)
        : undefined;
  return n !== undefined &&
    (USERS_PAGE_SIZE_OPTIONS as readonly number[]).includes(n)
    ? n
    : undefined;
};

const STATUS_SET = new Set<string>(accountStatuses);
const ROLE_SET = new Set<string>(roles);
const SORT_FIELD_SET = new Set<string>(['name', 'email', 'roles', 'status']);

const asSortBy = (v: unknown): AccountSortField | undefined =>
  typeof v === 'string' && SORT_FIELD_SET.has(v)
    ? (v as AccountSortField)
    : undefined;

const asSortOrder = (v: unknown): SortOrder | undefined =>
  v === 'asc' || v === 'desc' ? v : undefined;

// URL values arrive as strings, so `?includeDeleted=true` and a programmatic
// `true` both read as on. An explicit `false` must survive as `false` rather
// than collapsing to `undefined`: the Accounts table defaults the flag ON
// (`?? true`), so returning `undefined` for `'false'` would make the opt-out
// unreachable from the URL. Only absent/junk is `undefined`.
const asBool = (v: unknown): boolean | undefined => {
  if (v === true || v === 'true') return true;
  if (v === false || v === 'false') return false;
  return undefined;
};

const asStatusArray = (v: unknown): AccountStatus[] | undefined => {
  const arr = Array.isArray(v) ? v : v == null ? [] : [v];
  const filtered = arr.filter(
    (x): x is AccountStatus => typeof x === 'string' && STATUS_SET.has(x)
  );
  return filtered.length > 0 ? filtered : undefined;
};

const asRoleArray = (v: unknown): Role[] | undefined => {
  const arr = Array.isArray(v) ? v : v == null ? [] : [v];
  const filtered = arr.filter(
    (x): x is Role => typeof x === 'string' && ROLE_SET.has(x)
  );
  return filtered.length > 0 ? filtered : undefined;
};

export const validateUsersSearch = (
  input: Record<string, unknown>
): UsersSearch => ({
  search: asString(input.search),
  status: asStatusArray(input.status),
  roles: asRoleArray(input.roles),
  page: asPage(input.page),
  pageSize: asPageSize(input.pageSize),
  sortBy: asSortBy(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
  includeDeleted: asBool(input.includeDeleted),
});
